import React, { useEffect, useMemo, useState } from 'react'
import FullInterview from '../interview-engine/RoleInterview'
import CheckSetupModule from '../modules/check-setup/CheckSetup'
import type { InterviewData as ApiInterviewData, InterviewStatus, PrepareInterviewResponse } from '@/api/interviewApi'
import usePeerConnectionManager from '@/hooks/usePeerConnectionManager'
import MobilePeerConnectionContext from '@/context/MobilePeerConnectionContext'

type SetupCompletion = {
  interviewStatus?: InterviewStatus;
  interviewData?: ApiInterviewData;
  systemPromptStatus?: PrepareInterviewResponse['systemPromptStatus'];
  resolvedInitStatus?: InterviewStatus;
  preparedInSession?: boolean;
  resumeFromTimestamp?: string;
  screenStream?: MediaStream | null;
  mobilePeerConnectionId?: string | null;
  mobileCameraStream?: MediaStream | null;
  isMobileWebcamRequired?: boolean;
}

interface InterviewProps {
  id?: string;
  ID?: string; // Query parameter ID
}

const Interview: React.FC<InterviewProps> = ({ id, ID }) => {
  const [isSetupComplete, setIsSetupComplete] = useState(false);
  const [setupCompletion, setSetupCompletion] = useState<SetupCompletion | null>(null);

  const mobilePeerConnectionManager = usePeerConnectionManager();
  const {
    joinRoom,
    startPeerConnection,
    exitRoom,
    peerStatus,
    peerError,
    isPeerConnected,
    isSocketConnected,
    remoteStream,
    remoteStreamVersion,
    mobileHasJoined,
    lanMatchedPeerFailed,
    lanStatus,
    lanStatusDetail,
    isMobileBypassed,
    markMobileBypassed,
    sendPeerMessage,
  } = mobilePeerConnectionManager;

  const isMobileWebcamRequirementLoading = false;
  const isMobileWebcamRequiredForCurrentInterview = true;

  const interview_id = id || ID;
  const peerConnectionId = useMemo(() => {
    return `${interview_id}-interview`;
  }, [interview_id]);
  const resolvedPeerConnectionId = useMemo(() => {
    return setupCompletion?.mobilePeerConnectionId ?? peerConnectionId ?? null;
  }, [setupCompletion?.mobilePeerConnectionId, peerConnectionId]);


  // weh check setup is complete we are setting up the setup completion state
  const handleSetupComplete = (payload?: Omit<SetupCompletion, 'isMobileWebcamRequired'>) => {
    const resolvedStream = payload?.mobileCameraStream ?? remoteStream ?? null;
    if (payload) {
      setSetupCompletion({
        ...payload,
        mobilePeerConnectionId: payload.mobilePeerConnectionId ?? peerConnectionId ?? null,
        mobileCameraStream: resolvedStream,
        isMobileWebcamRequired: isMobileWebcamRequiredForCurrentInterview,
      });
    } else {
      setSetupCompletion({
        mobilePeerConnectionId: peerConnectionId ?? null,
        mobileCameraStream: resolvedStream,
        isMobileWebcamRequired: isMobileWebcamRequiredForCurrentInterview,
      });
    }
    setIsSetupComplete(true);
  };

  useEffect(() => {
    if (!resolvedPeerConnectionId)  return;
    joinRoom(resolvedPeerConnectionId, "laptop");
  }, [joinRoom, resolvedPeerConnectionId]);

  /*
  --------------------------------------------------------------
  As Long as socket is connected we are starting the peer connection
  --------------------------------------------------------------
  */
  useEffect(() => {
    if (!isSocketConnected)  return;
    if (!resolvedPeerConnectionId)  return;
    if (peerStatus === "idle" || peerStatus === "closed" || peerStatus === "error") {
      void startPeerConnection({ initiator: false });
    }
  }, [
    isSocketConnected,
    peerStatus,
    resolvedPeerConnectionId,
    startPeerConnection,
  ]);

const activeMobileStream = useMemo(() => {
    return remoteStream ?? setupCompletion?.mobileCameraStream ?? null;
  }, [remoteStream, setupCompletion?.mobileCameraStream]);

  /*
  --------------------------------------------------------------
  we are getting the latest mobile stream key from the remote stream (useful when mobile stream is changed while reconnecting)
  --------------------------------------------------------------
  */
  const latestMobileStreamKeyFromRemoteStream = useMemo(() => {
    if (!activeMobileStream) {
      return `no-mobile-stream-${remoteStreamVersion}`;
    }
    const trackIds = activeMobileStream.getTracks().map((track) => track.id).join("-");
    return `${remoteStreamVersion}-${activeMobileStream.id}-${trackIds}`;
  }, [activeMobileStream, remoteStreamVersion]);

  const mobilePeerContextValue = useMemo(
    () => ({
      peerConnectionId: resolvedPeerConnectionId,
      peerStatus,
      peerError,
      isPeerConnected,
      isSocketConnected,
      remoteStream,
      remoteStreamVersion,
      mobileHasJoined,
      lanMatchedPeerFailed,
      lanStatus,
      lanStatusDetail,
      isMobileBypassed,
      markMobileBypassed,
      sendPeerMessage,
    }),
    [
      resolvedPeerConnectionId,
      peerStatus,
      peerError,
      isPeerConnected,
      isSocketConnected,
      remoteStream,
      remoteStreamVersion,
      mobileHasJoined,
      lanMatchedPeerFailed,
      lanStatus,
      lanStatusDetail,
      isMobileBypassed,
      markMobileBypassed,
      sendPeerMessage,
    ],
  );

  useEffect(() => {
    return () => {
      exitRoom(true);
    };
  }, [exitRoom]);

  return (
    <MobilePeerConnectionContext.Provider value={mobilePeerContextValue}>
    <div className='w-full h-screen'>
      {isSetupComplete ? (
        <FullInterview
          interview_id={interview_id}
          initialInterviewStatus={setupCompletion?.resolvedInitStatus ?? setupCompletion?.interviewStatus}
          interviewData={setupCompletion?.interviewData}
          systemPromptStatus={setupCompletion?.systemPromptStatus}
          preparedInSession={setupCompletion?.preparedInSession}
          resumeFromTimestamp={setupCompletion?.resumeFromTimestamp}
          screenStream={setupCompletion?.screenStream ?? null}
          mobileCameraStream={activeMobileStream}
          mobileStreamKey={latestMobileStreamKeyFromRemoteStream}
          isMobileWebcamRequired={isMobileWebcamRequiredForCurrentInterview}
        />
      ) : (
        <CheckSetupModule 
          interview_id={interview_id}
          isMobileWebcamRequired={isMobileWebcamRequiredForCurrentInterview}
          isMobileWebcamRequirementLoading={isMobileWebcamRequirementLoading}
          onComplete={handleSetupComplete} 
        />
      )}
    </div>
    </MobilePeerConnectionContext.Provider>
  )
}

export default Interview
